import { templateService } from "@/services/template.service";
import { TemplateDto } from "@exibra/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isNil from "lodash.isnil";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const templateQueryKeys = {
    customTemplates: (tenantId?: number) => `customTemplates-${tenantId}`,
    customTemplateById: (templateId: number) => `customTemplateById-${templateId}`,
} as const;

/**
 * Get custom templates for a tenant
 * @param tenantId - The ID of the tenant to get custom templates for
 * @returns The custom templates for the tenant
 */
export const useCustomTemplates = (tenantId?: number) => {
    return useQuery({
        queryKey: [templateQueryKeys.customTemplates(tenantId)],
        queryFn: () => templateService.getCustomTemplates(tenantId!),
        enabled: !isNil(tenantId),
    });
};

/**
 * Save a custom template
 * @param template - The template to save
 * @returns The saved template
 */
export const useSaveTemplate = () => {
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ template, isCreate }: { template: TemplateDto; isCreate?: boolean }) =>
            templateService.saveTemplate(template, isCreate ?? false),
        onSuccess: (_, variables) => {
            toast.success(
                t(`templateEditor.${variables.isCreate ? "created" : "updated"}`, {
                    templateName: variables.template.name,
                }),
            );
        },
        onError: () => {
            toast.error(t("templateEditor.error"));
        },
    });
};

/**
 * Delete a custom template
 * @param id - The ID of the template to delete
 * @returns The deleted template
 */
export const useDeleteTemplate = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ templateId }: { templateId: number; tenantId: number }) =>
            templateService.deleteTemplate(templateId),
        onSuccess: (_, variables) => {
            toast.success(t("templateEditor.deleted"));
            queryClient.invalidateQueries({
                queryKey: [templateQueryKeys.customTemplates(variables.tenantId)],
            });
        },
        onError: () => {
            toast.error(t("templateEditor.error"));
        },
    });
};

/**
 * Get a custom template by ID
 * @param templateId - The ID of the template to get
 * @returns The custom template
 */
export const useGetTemplateById = (templateId?: number) => {
    return useQuery({
        queryKey: [templateQueryKeys.customTemplateById(templateId!)],
        queryFn: () => templateService.getTemplateById(templateId!),
        enabled: !isNil(templateId),
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
};

/**
 * Duplicate a template
 * @returns The duplicated template
 */
export const useDuplicateTemplate = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ templateId }: { templateId: number; tenantId: number }) =>
            templateService.duplicateTemplate(templateId),
        onSuccess: (_, variables) => {
            toast.success(t("templates.success.duplicate"));
            queryClient.invalidateQueries({
                queryKey: [templateQueryKeys.customTemplates(variables.tenantId)],
            });
        },
        onError: () => {
            toast.error(t("templates.error.duplicate"));
        },
    });
};

/**
 * Migrate a v1 template to a v2 (slideshow) format. The source template is left untouched —
 * the mutation returns the new copy.
 */
export const useMigrateTemplateToV2 = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ templateId }: { templateId: number; tenantId: number }) =>
            templateService.migrateToV2(templateId),
        onSuccess: (_, variables) => {
            toast.success(t("templates.success.migrated", "Migrated to v2"));
            queryClient.invalidateQueries({
                queryKey: [templateQueryKeys.customTemplates(variables.tenantId)],
            });
        },
        onError: () => {
            toast.error(t("templates.error.migrate", "Failed to migrate template"));
        },
    });
};
